import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cardService, watchlistService, priceSubmissionService, marketplaceListingService, auctionService, PriceStats, PriceHistoryPoint, User } from '../services/api';
import ReportPriceModal from '../components/ReportPriceModal';
import ReportListingModal from '../components/ReportListingModal';
import PriceHistoryChart from '../components/PriceHistoryChart';
import { formatDistanceToNow } from 'date-fns';

interface MarketplaceListing {
  id: string;
  price: number;
  condition: string;
  quantity: number;
  listingType: 'WTS' | 'WTB';
  imageUrls: string;
  seller: {
    id: number;
    displayName: string;
    profilePicture?: string;
    averageRating?: number;
    totalReviews?: number;
  };
  createdAt: string;
  externalLink?: string;
}

interface Auction {
  id: number;
  title?: string;
  status: string;
  currentPrice: number;
  endTime: string;
  imageUrls: string;
  seller: {
    id: number;
    displayName: string;
  };
}

interface Card {
  id: any;
  name: string;
  language?: string;
  imageUrl?: string;
  imageBlobPath?: string;
  [key: string]: any;
}

// Helper to render stars
const renderStars = (rating: number) => (
  <div className="flex items-center text-xs">
    {[1, 2, 3, 4, 5].map((star) => (
      <span key={star} className={`${star <= Math.round(rating) ? 'text-amber-500' : 'text-gray-300'}`}>
        ★
      </span>
    ))}
  </div>
);

const CardDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  // navigate removed as it is no longer used
  const [card, setCard] = useState<Card | null>(null);
  const [loading, setLoading] = useState(true);
  const [addingToWatchlist, setAddingToWatchlist] = useState(false);
  const [watchlistMessage, setWatchlistMessage] = useState('');
  const [isReportPriceModalOpen, setIsReportPriceModalOpen] = useState(false);
  const [priceStats, setPriceStats] = useState<PriceStats | null>(null);
  const [loadingPriceStats, setLoadingPriceStats] = useState(false);
  const [priceHistory, setPriceHistory] = useState<PriceHistoryPoint[]>([]);
  const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [reportListingId, setReportListingId] = useState<string | number | null>(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  useEffect(() => {
    const fetchCard = async () => {
      if (!id) return;

      try {
        const response = await cardService.getCard(id);
        setCard(response.data.data || response.data);
      } catch (error) {
        console.error('Failed to fetch card:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCard();
    fetchPriceStats();
    fetchPriceHistory();
    fetchListings();
    fetchAuctions();
  }, [id]);

  const fetchListings = async () => {
    if (!id) return;

    setLoadingListings(true);
    try {
      const response = await marketplaceListingService.getListings({ cardId: id, limit: 20 });
      setListings(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoadingListings(false);
    }
  };

  const fetchAuctions = async () => {
    if (!id) return;
    try {
      const response = await auctionService.getAuctions({ cardId: id, status: 'LIVE', limit: 5 });
      setAuctions(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch auctions', err);
    }
  };

  const fetchPriceStats = async () => {
    if (!id) return;

    setLoadingPriceStats(true);
    try {
      const response = await priceSubmissionService.getCardPriceStats(id);
      setPriceStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch price stats:', error);
    } finally {
      setLoadingPriceStats(false);
    }
  };

  const fetchPriceHistory = async () => {
    if (!id) return;

    setLoadingPriceHistory(true);
    try {
      const response = await priceSubmissionService.getCardPriceHistory(id, 90);
      setPriceHistory(response.data.data);
    } catch (error) {
      console.error('Failed to fetch price history:', error);
    } finally {
      setLoadingPriceHistory(false);
    }
  };

  const handlePriceSubmitSuccess = () => {
    setWatchlistMessage(t('cardDetail.priceSubmitted'));
    // Stay on page and refresh data
    setTimeout(() => {
      setWatchlistMessage('');
    }, 3000);
    fetchPriceStats(); // Refresh price stats
    fetchPriceHistory(); // Refresh price history
  };

  const [isWatched, setIsWatched] = useState(false);
  const [watcherCount, setWatcherCount] = useState(0);

  useEffect(() => {
    fetchWatchlistStatus();
  }, [id, currentUser]); // Re-fetch if user changes (login/logout)

  const fetchWatchlistStatus = async () => {
    if (!id) return;
    try {
      const response = await watchlistService.getWatchlistStatus(id);
      setIsWatched(response.data.data.isWatched);
      setWatcherCount(response.data.data.count);
    } catch (error) {
      console.error('Failed to fetch watchlist status:', error);
    }
  };

  const handleToggleWatchlist = async () => {
    if (!id) return;

    if (!currentUser) {
      // Optional: Redirect to login or show modal
      alert(t('cardDetail.loginRequired'));
      return;
    }

    setAddingToWatchlist(true);
    setWatchlistMessage('');

    try {
      if (isWatched) {
        await watchlistService.removeFromWatchlist(id);
        setIsWatched(false);
        setWatcherCount(prev => Math.max(0, prev - 1));
        setWatchlistMessage(t('cardDetail.removedWatchlist'));
      } else {
        await watchlistService.addToWatchlist(id);
        setIsWatched(true);
        setWatcherCount(prev => prev + 1);
        setWatchlistMessage(t('cardDetail.successWatchlist'));
      }
    } catch (error: any) {
      setWatchlistMessage(error.response?.data?.message || 'Failed to update watchlist');
    } finally {
      setAddingToWatchlist(false);
      // Clear message after 3 seconds
      setTimeout(() => setWatchlistMessage(''), 3000);
    }
  };


  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading')}</div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Card not found</div>
      </div>
    );
  }

  // Construct image URL
  const imageUrl = card.imageUrl || (card.imageBlobPath ? card.imageBlobPath : null);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid md:grid-cols-2 gap-8">
        {/* Card Image */}
        <div className="flex items-start justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={card.name}
              className="w-3/4 md:w-full max-w-md rounded-lg shadow-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div className={`aspect-square bg-gray-200 rounded-lg flex items-center justify-center text-gray-500 w-3/4 md:w-full max-w-md ${imageUrl ? 'hidden' : ''}`}>
            {t('cardDetail.noImage')}
          </div>
        </div>

        {/* Card Details */}
        <div>
          <h1 className="text-3xl font-bold mb-4 flex items-center gap-2 flex-wrap">
            {card.name || 'Card Name'}
            {card.language && (
              <span className={`text-lg px-3 py-1 rounded-full border ${card.language === 'EN'
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-gray-50 text-gray-600 border-gray-200'
                }`}>
                {card.language}
              </span>
            )}
          </h1>

          {/* Price Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">{t('cardDetail.communityPrice')}</h2>
              <div className="flex items-center gap-2">
                {currentUser && (currentUser.role === 'admin' || currentUser.role === 'moderator' || currentUser.role === 'advanced_user') && !currentUser.isBannedFromReporting && (
                  <button
                    onClick={() => setIsReportPriceModalOpen(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    {t('cardDetail.reportPrice')}
                  </button>
                )}
                <Link
                  to="/how-to-report"
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-700 text-xs font-bold transition-colors"
                  title={t('cardDetail.howToReport')}
                >
                  ?
                </Link>
              </div>
            </div>

            {loadingPriceStats ? (
              <p className="text-gray-600">{t('cardDetail.loadingPrice')}</p>
            ) : priceStats?.hasData ? (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <p className="text-3xl font-bold text-blue-600">
                    ฿{priceStats.avgPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  {priceStats.confidence && (
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${priceStats.confidence === 'high'
                        ? 'bg-green-100 text-green-700'
                        : priceStats.confidence === 'medium'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-orange-100 text-orange-700'
                        }`}
                    >
                      {t('cardDetail.confidence', { level: priceStats.confidence })}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
                  <div>
                    <span className="text-gray-600">{t('cardDetail.low')}</span>{' '}
                    <span className="font-semibold">฿{priceStats.minPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('cardDetail.high')}</span>{' '}
                    <span className="font-semibold">฿{priceStats.maxPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  {t('cardDetail.basedOn', { count: priceStats.totalSubmissions })}{' '}
                  {t('cardDetail.recentSubmissions', { count: priceStats.recentSubmissions })}
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-2">{t('cardDetail.noPriceData')}</p>
                <p className="text-xs text-gray-500">
                  {t('cardDetail.beTheFirst')}
                </p>
              </>
            )}
          </div>

          {/* Card Details */}
          <div className="space-y-3 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('cardDetail.cardNumber')}</p>
                <p className="font-semibold">{card.cardNumber || t('cardDetail.na')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('cardDetail.type')}</p>
                <p className="font-semibold">{card.type || t('cardDetail.na')}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('cardDetail.rarity')}</p>
                <p className="font-semibold">{card.rarity || t('cardDetail.na')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('cardDetail.color')}</p>
                <p className="font-semibold">{card.color || t('cardDetail.na')}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('cardDetail.setName')}</p>
                <p className="font-semibold">{card.setName || t('cardDetail.na')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('cardDetail.symbol')}</p>
                <p className="font-semibold">{card.symbol || t('cardDetail.na')}</p>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-500">{t('cardDetail.soi')}</p>
              <p className="font-semibold">{card.soi || t('cardDetail.na')}</p>
            </div>

            {card.abilityText && (
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('cardDetail.abilityText')}</p>
                <p className="text-sm bg-gray-50 p-3 rounded border border-gray-200">
                  {card.abilityText}
                </p>
              </div>
            )}
          </div>

          {/* Add to Watchlist Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleToggleWatchlist}
              disabled={addingToWatchlist}
              className={`flex-1 py-3 rounded-lg transition font-semibold flex items-center justify-center gap-2 ${isWatched
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {addingToWatchlist ? (
                <span>{t('cardDetail.adding')}</span>
              ) : (
                <>
                  {isWatched ? (
                    <>
                      <span>✓</span>
                      <span>{t('cardDetail.watching')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('cardDetail.addToWatchlist')}</span>
                    </>
                  )}
                </>
              )}
            </button>

            <div className="flex flex-col items-center justify-center bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 min-w-[80px]">
              <span className="text-xl font-bold text-gray-800">{watcherCount}</span>
              <span className="text-xs text-gray-500">{t('cardDetail.watchers')}</span>
            </div>
          </div>

          {watchlistMessage && (
            <p className={`mt-2 text-sm text-center ${watchlistMessage.includes('success') || watchlistMessage.includes('remove') ? 'text-green-600' : 'text-red-600'}`}>
              {watchlistMessage}
            </p>
          )}
        </div>
      </div>

      {/* Live Auctions Section */}
      {auctions.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-4">🔥 Live Auctions</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {auctions.map(auction => (
              <Link key={auction.id} to={`/auctions/${auction.id}`} className="block border rounded-lg p-4 hover:shadow-md transition bg-white group">
                <div className="flex justify-between items-start">
                  <h3 className="font-semibold text-lg text-gray-800 group-hover:text-blue-600">{auction.title || card.name}</h3>
                  <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded font-bold">LIVE</span>
                </div>
                <p className="text-gray-500 text-sm mt-1">by {auction.seller.displayName}</p>
                <div className="mt-3 flex justify-between items-center">
                  <p className="text-lg font-bold text-blue-700">{auction.currentPrice.toLocaleString()} THB</p>
                  <p className="text-xs text-gray-500">{formatDistanceToNow(new Date(auction.endTime), { addSuffix: true })}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}


      {/* Price History Section */}
      < div className="mt-12" >
        <h2 className="text-2xl font-bold mb-4">{t('cardDetail.priceHistory')}</h2>
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <PriceHistoryChart data={priceHistory} loading={loadingPriceHistory} />
        </div>
      </div >

      {/* Marketplace Listings Section */}
      < div className="mt-12" >
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <h2 className="text-2xl font-bold">{t('cardDetail.marketplaceListings')}</h2>
          <div className="flex gap-2">
            {currentUser && (
              <>
                <Link
                  to={`/marketplace/sell?type=WTS&cardId=${id}`}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium flex items-center gap-1"
                >
                  <span>+</span> {t('cardDetail.sell')}
                </Link>
                <Link
                  to={`/marketplace/sell?type=WTB&cardId=${id}`}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm font-medium flex items-center gap-1"
                >
                  <span>+</span> {t('cardDetail.buy')}
                </Link>
              </>
            )}
          </div>
        </div>

        {
          loadingListings ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-600">{t('cardDetail.loadingListings')}</p>
            </div>
          ) : listings.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-600">{t('cardDetail.noListings')}</p>
              <p className="text-sm text-gray-500 mt-2">{t('cardDetail.beTheFirstListing')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* WTS Listings */}
              {listings.filter(l => l.listingType === 'WTS' || !l.listingType).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-blue-600">{t('cardDetail.selling')}</h3>
                  <div className="grid gap-4">
                    {listings
                      .filter(l => l.listingType === 'WTS' || !l.listingType)
                      .map((listing) => {

                        return (
                          <div key={listing.id} className="relative group">
                            <Link
                              to={`/marketplace/listing/${listing.id}`}
                              className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition"
                            >
                              <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={card.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                    {t('cardDetail.noImg')}
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-1">
                                  {/* Seller Avatar */}
                                  {listing.seller.profilePicture ? (
                                    <img
                                      src={listing.seller.profilePicture}
                                      alt={listing.seller.displayName}
                                      className="w-8 h-8 rounded-full object-cover border border-gray-200"
                                    />
                                  ) : (
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border border-gray-200 ${listing.listingType === 'WTB' ? 'bg-green-600' : 'bg-blue-600'}`}>
                                      {listing.seller.displayName.charAt(0).toUpperCase()}
                                    </div>
                                  )}

                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-sm">{listing.seller.displayName}</span>
                                      {/* Rating */}
                                      <div className="flex items-center gap-1">
                                        {renderStars(listing.seller.averageRating || 0)}
                                        <span className="text-xs text-gray-500">
                                          ({listing.seller.totalReviews || 0})
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      Qty: {listing.quantity} • {new Date(listing.createdAt).toLocaleDateString('th-TH')}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-start gap-4 h-full">
                                {/* Facebook Button (Left) */}
                                <div className="self-center">
                                  {listing.externalLink ? (
                                    <a
                                      href={listing.externalLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1877F2] text-white text-xs font-medium rounded hover:bg-[#166fe5] transition-colors shadow-sm"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                                      </svg>
                                      {t('common.facebookButton')}
                                    </a>
                                  ) : (
                                    <button
                                      disabled
                                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-400 text-xs font-medium rounded cursor-not-allowed shadow-sm border border-gray-200"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 opacity-50">
                                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                                      </svg>
                                      {t('common.facebookButton')}
                                    </button>
                                  )}
                                </div>

                                {/* Price & Report (Right) */}
                                <div className="flex flex-col items-end gap-1 min-w-[100px]">
                                  <div className="text-right">
                                    <div className="text-xl font-bold text-blue-600">
                                      {listing.price.toLocaleString('en-US', { maximumFractionDigits: 0 })} THB
                                    </div>
                                    <div className="text-xs text-gray-500">{t('cardDetail.perCard')}</div>
                                  </div>

                                  {currentUser && (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setReportListingId(listing.id);
                                      }}
                                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-red-50 text-red-600 text-[10px] font-medium rounded border border-red-100 hover:bg-red-100 transition-colors mt-1 relative z-20 cursor-pointer"
                                      title={t('marketplace.reportListing')}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                      </svg>
                                      {t('marketplace.reportToClose')}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </Link>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* WTB Listings */}
              {listings.filter(l => l.listingType === 'WTB').length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-green-600">{t('cardDetail.buying')}</h3>
                  <div className="grid gap-4">
                    {listings
                      .filter(l => l.listingType === 'WTB')
                      .map((listing) => {

                        return (
                          <div key={listing.id} className="relative group bg-white border border-gray-200 rounded-lg hover:border-green-300 hover:shadow-md transition p-4 flex items-center gap-4">
                            <Link
                              to={`/marketplace/listing/${listing.id}`}
                              className="absolute inset-0 z-0"
                            />

                            <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0 pointer-events-none z-10 relative">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={card.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                  {t('cardDetail.noImg')}
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0 pointer-events-none z-10 relative">
                              <div className="flex items-center gap-3 mb-1">
                                {/* Seller Avatar */}
                                {listing.seller.profilePicture ? (
                                  <img
                                    src={listing.seller.profilePicture}
                                    alt={listing.seller.displayName}
                                    className="w-8 h-8 rounded-full object-cover border border-gray-200"
                                  />
                                ) : (
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border border-gray-200 ${listing.listingType === 'WTB' ? 'bg-green-600' : 'bg-blue-600'}`}>
                                    {listing.seller.displayName.charAt(0).toUpperCase()}
                                  </div>
                                )}

                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-sm">{listing.seller.displayName}</span>
                                    {/* Rating */}
                                    <div className="flex items-center gap-1">
                                      {renderStars(listing.seller.averageRating || 0)}
                                      <span className="text-xs text-gray-500">
                                        ({listing.seller.totalReviews || 0})
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    Qty: {listing.quantity} • {new Date(listing.createdAt).toLocaleDateString('th-TH')}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-start gap-4 h-full">
                              {/* Facebook Button (Left) */}
                              <div className="self-center">
                                {listing.externalLink ? (
                                  <a
                                    href={listing.externalLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1877F2] text-white text-xs font-medium rounded hover:bg-[#166fe5] transition-colors shadow-sm"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                                    </svg>
                                    {t('common.facebookButton')}
                                  </a>
                                ) : (
                                  <button
                                    disabled
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-400 text-xs font-medium rounded cursor-not-allowed shadow-sm border border-gray-200"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 opacity-50">
                                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                                    </svg>
                                    {t('common.facebookButton')}
                                  </button>
                                )}
                              </div>

                              {/* Price & Report (Right) */}
                              <div className="flex flex-col items-end gap-1 min-w-[100px]">
                                <div className="text-right">
                                  <div className="text-xl font-bold text-green-600">
                                    {listing.price.toLocaleString('en-US', { maximumFractionDigits: 0 })} THB
                                  </div>
                                  <div className="text-xs text-gray-500">{t('cardDetail.perCard')}</div>
                                </div>

                                {currentUser && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setReportListingId(listing.id);
                                    }}
                                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-red-50 text-red-600 text-[10px] font-medium rounded border border-red-100 hover:bg-red-100 transition-colors mt-1 relative z-20 cursor-pointer"
                                    title={t('marketplace.reportListing')}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                    </svg>
                                    {t('marketplace.reportToClose')}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )
        }
      </div >

      {/* Report Price Modal */}
      < ReportPriceModal
        cardId={card.id}
        cardName={card.name}
        isOpen={isReportPriceModalOpen}
        onClose={() => setIsReportPriceModalOpen(false)}
        onSuccess={handlePriceSubmitSuccess}
      />

      <ReportListingModal
        isOpen={!!reportListingId}
        onClose={() => setReportListingId(null)}
        listingId={reportListingId}
        onSuccess={() => {
          // Optional: Refresh listings or show toast
          alert(t('marketplace.reportSuccess'));
        }}
      />
    </div >
  );
};

export default CardDetailPage;
